import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { identityApi, type IdentityRecord } from '../../api/identity';

export function IdentityDirectoryPage() {
  const [items, setItems] = useState<IdentityRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await identityApi.search({ q: query, limit: '50' });
      setItems(res.items ?? []);
      setTotal(res.total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load identities');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setError('');
    try {
      await identityApi.create({ email, display_name: displayName });
      setEmail('');
      setDisplayName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    }
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold text-slate-900">Identity Directory</h1>
      <p className="mt-1 text-sm text-slate-600">Canonical user identities for the platform.</p>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <form onSubmit={onCreate} className="mt-6 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-3">
        <input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <input required placeholder="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <button type="submit" className="rounded-lg bg-slate-900 px-4 py-2 text-white md:col-span-3 md:w-fit">Create identity</button>
      </form>

      <div className="mt-6 flex gap-2">
        <Link to="/identity/search" className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Advanced search</Link>
      </div>

      <div className="mt-4 flex gap-2">
        <input placeholder="Search" value={query} onChange={(e) => setQuery(e.target.value)} className="flex-1 rounded-lg border border-slate-300 px-3 py-2" />
        <button type="button" onClick={() => void load()} className="rounded-lg border border-slate-300 px-4 py-2">Search</button>
      </div>

      <p className="mt-4 text-sm text-slate-500">{total} identities</p>
      {loading ? (
        <p className="mt-4 text-slate-600">Loading…</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {items.map((item) => (
            <li key={item.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Link to={`/identity/${item.id}`} className="font-medium text-slate-900 hover:underline">{item.display_name}</Link>
                  <p className="text-sm text-slate-600">{item.email}</p>
                  <p className="mt-1 text-xs text-slate-500">{item.status} · {item.type}</p>
                </div>
                <div className="flex gap-3 text-sm">
                  <Link to={`/identity/${item.id}/profile`} className="text-slate-600 hover:underline">Profile</Link>
                  <Link to={`/identity/${item.id}/preferences`} className="text-slate-600 hover:underline">Preferences</Link>
                  <Link to={`/identity/${item.id}/activity`} className="text-slate-600 hover:underline">Activity</Link>
                  <Link to={`/identity/${item.id}/audit`} className="text-slate-600 hover:underline">Audit</Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
